import { useMemo, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Tag, Check, X, Pencil, Sparkles, Loader2, RotateCw } from "lucide-react";
import { AiDisclosure } from "@/components/AiDisclosure";

// Shared confirm-to-apply label suggestion surface. Reused wherever photos are reviewed
// (standard Capture, Quick Start review). Drafts are never saved until the user confirms.
export function PhotoLabelSuggestions({ propertyId, photos, readOnly = false, dark = false, onChanged }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [dismissed, setDismissed] = useState({});   // photo_id -> true (session-only, no evidence change)
  const [selected, setSelected] = useState({});     // photo_id -> true
  const [editing, setEditing] = useState({});        // photo_id -> draft text
  const [savingId, setSavingId] = useState(null);

  const eligible = useMemo(
    () => (photos || []).filter((p) => p && p.photo_id && !p.is_deleted),
    [photos],
  );

  if (!eligible.length) return null;

  const needsLabel = eligible.filter((p) => !p.caption && !(p.label_suggestion_status === "suggested" && !dismissed[p.photo_id]));
  const suggestedDrafts = eligible.filter((p) => p.label_suggestion_status === "suggested" && p.label_suggestion && !p.caption && !dismissed[p.photo_id]);

  const runSuggest = async () => {
    const ids = eligible.filter((p) => !p.caption).map((p) => p.photo_id).slice(0, 10);
    if (!ids.length) { toast.info("All photos already have labels."); return; }
    setLoading(true); setError(false);
    try {
      await api.post("/photos/label-suggestions", { property_id: propertyId, photo_ids: ids });
      onChanged && (await onChanged());
    } catch (e) {
      if (e?.response?.status === 429) toast.error(e.response.data?.detail || "Too many requests — try later.");
      else setError(true);
    } finally { setLoading(false); }
  };

  const confirmOne = async (p, text) => {
    const caption = (text ?? p.label_suggestion ?? "").trim();
    if (!caption) return;
    setSavingId(p.photo_id);
    try {
      await api.patch(`/photos/${p.photo_id}/caption`, { caption });
      setEditing((s) => { const n = { ...s }; delete n[p.photo_id]; return n; });
      setSelected((s) => { const n = { ...s }; delete n[p.photo_id]; return n; });
      onChanged && (await onChanged());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the label.");
    } finally { setSavingId(null); }
  };

  const confirmSelected = async () => {
    const chosen = suggestedDrafts.filter((p) => selected[p.photo_id]);
    if (!chosen.length) { toast.info("Select at least one suggestion first."); return; }
    for (const p of chosen) {
      // eslint-disable-next-line no-await-in-loop
      await confirmOne(p, editing[p.photo_id]);
    }
    toast.success(`Saved ${chosen.length} label${chosen.length > 1 ? "s" : ""}.`);
  };

  const dismissOne = (p) => setDismissed((s) => ({ ...s, [p.photo_id]: true }));

  const cardCls = dark
    ? "border-white/10 bg-white/5"
    : "border-[#E2E8F0] bg-white";
  const textMuted = dark ? "text-white/60" : "text-[#64748B]";
  const textMain = dark ? "text-white" : "text-navy";

  return (
    <div data-testid="label-suggestions" className={`rounded-2xl border ${cardCls} p-4`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`grid place-items-center h-8 w-8 rounded-lg ${dark ? "bg-white/10 text-amber" : "bg-brand/10 text-brand"} shrink-0`}><Tag size={16} /></span>
          <div className="min-w-0">
            <p className={`font-semibold text-sm ${textMain}`}>Photo labels</p>
            <p className={`text-[11px] ${textMuted} leading-snug`}>Optional location labels — you review and save each one.</p>
          </div>
        </div>
        {!readOnly && (
          <Button data-testid="label-suggest-btn" onClick={runSuggest} disabled={loading}
            className={`h-9 rounded-xl shrink-0 ${dark ? "bg-amber hover:bg-amber-hover text-navy" : "bg-navy hover:bg-navy-hover text-white"}`}>
            {loading ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Sparkles size={15} className="mr-1.5" />} Suggest labels
          </Button>
        )}
      </div>

      {error && (
        <div data-testid="label-suggest-error" className={`mt-3 rounded-xl border ${dark ? "border-red-400/40 bg-red-400/10" : "border-red-200 bg-red-50"} p-3`}>
          <p className={`text-sm font-medium ${dark ? "text-red-200" : "text-red-700"}`}>Label suggestions couldn’t load.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button data-testid="label-retry-btn" onClick={runSuggest} className="h-8 rounded-lg bg-navy hover:bg-navy-hover text-white text-xs"><RotateCw size={13} className="mr-1" /> Retry</Button>
            <Button data-testid="label-manual-btn" variant="outline" onClick={() => setError(false)} className="h-8 rounded-lg text-xs">Label manually</Button>
            <Button data-testid="label-error-dismiss-btn" variant="ghost" onClick={() => setError(false)} className="h-8 rounded-lg text-xs">Dismiss</Button>
          </div>
        </div>
      )}

      {suggestedDrafts.length > 0 && !readOnly && (
        <div className="mt-3 flex items-center justify-between">
          <span className={`text-[11px] font-semibold ${textMuted}`}>{suggestedDrafts.length} suggestion{suggestedDrafts.length > 1 ? "s" : ""} to review</span>
          <Button data-testid="label-confirm-selected-btn" onClick={confirmSelected}
            className="h-8 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs">
            <Check size={13} className="mr-1" /> Confirm selected
          </Button>
        </div>
      )}

      {suggestedDrafts.length > 0 && (
        <AiDisclosure className={`mt-3 ${dark ? "text-white/50" : ""}`} />
      )}

      <div className="mt-3 space-y-2.5">
        {eligible.map((p) => {
          const isSuggested = p.label_suggestion_status === "suggested" && p.label_suggestion && !p.caption && !dismissed[p.photo_id];
          const isUnavailable = p.label_suggestion_status === "unavailable" && !p.caption && !dismissed[p.photo_id];
          const isEditing = editing[p.photo_id] !== undefined;
          return (
            <div key={p.photo_id} data-testid={`label-row-${p.photo_id}`}
              className={`flex items-start gap-3 rounded-xl border p-2.5 ${isSuggested ? (dark ? "border-dashed border-amber/50 bg-amber/[0.06]" : "border-dashed border-brand/50 bg-brand/[0.04]") : (dark ? "border-white/10" : "border-[#E2E8F0]")}`}>
              <img src={fileUrl(p.storage_path)} alt="" className="h-12 w-12 rounded-lg object-cover border border-black/10 shrink-0" />
              <div className="min-w-0 flex-1">
                {p.caption ? (
                  isEditing ? (
                    <EditRow dark={dark} value={editing[p.photo_id]} onChange={(v) => setEditing((s) => ({ ...s, [p.photo_id]: v }))}
                      onSave={() => confirmOne(p, editing[p.photo_id])} onCancel={() => setEditing((s) => { const n = { ...s }; delete n[p.photo_id]; return n; })}
                      saving={savingId === p.photo_id} pid={p.photo_id} />
                  ) : (
                    <div className="flex items-center justify-between gap-2">
                      <span data-testid={`label-confirmed-${p.photo_id}`} className={`text-sm font-medium ${textMain} truncate`}>{p.caption}</span>
                      {!readOnly && (
                        <button data-testid={`label-edit-confirmed-${p.photo_id}`} onClick={() => setEditing((s) => ({ ...s, [p.photo_id]: p.caption }))} className={`text-xs ${dark ? "text-white/60" : "text-[#64748B]"} hover:underline shrink-0`}>
                          <Pencil size={12} className="inline mr-0.5" /> Edit
                        </button>
                      )}
                    </div>
                  )
                ) : isSuggested ? (
                  <>
                    <p className={`text-[10px] font-semibold uppercase tracking-wide ${dark ? "text-amber/80" : "text-brand/80"}`}>Suggested label — review before saving</p>
                    {isEditing ? (
                      <EditRow dark={dark} value={editing[p.photo_id]} onChange={(v) => setEditing((s) => ({ ...s, [p.photo_id]: v }))}
                        onSave={() => confirmOne(p, editing[p.photo_id])} onCancel={() => setEditing((s) => { const n = { ...s }; delete n[p.photo_id]; return n; })}
                        saving={savingId === p.photo_id} pid={p.photo_id} />
                    ) : (
                      <>
                        <div className="flex items-center gap-2 mt-0.5">
                          {!readOnly && (
                            <input type="checkbox" data-testid={`label-select-${p.photo_id}`} checked={!!selected[p.photo_id]}
                              onChange={(e) => setSelected((s) => ({ ...s, [p.photo_id]: e.target.checked }))}
                              className="h-4 w-4 rounded accent-emerald-600 shrink-0" />
                          )}
                          <span data-testid={`label-draft-${p.photo_id}`} className={`text-sm font-medium ${textMain} truncate`}>{p.label_suggestion}</span>
                        </div>
                        {!readOnly && (
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            <button data-testid={`label-confirm-${p.photo_id}`} onClick={() => confirmOne(p)} disabled={savingId === p.photo_id}
                              className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-2.5 py-1">
                              {savingId === p.photo_id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Confirm
                            </button>
                            <button data-testid={`label-edit-${p.photo_id}`} onClick={() => setEditing((s) => ({ ...s, [p.photo_id]: p.label_suggestion }))}
                              className={`inline-flex items-center gap-1 rounded-lg border text-xs font-medium px-2.5 py-1 ${dark ? "border-white/15 text-white/80" : "border-[#E2E8F0] text-navy"}`}>
                              <Pencil size={12} /> Edit
                            </button>
                            <button data-testid={`label-dismiss-${p.photo_id}`} onClick={() => dismissOne(p)}
                              className={`inline-flex items-center gap-1 rounded-lg text-xs px-2 py-1 ${dark ? "text-white/50" : "text-[#94A3B8]"}`}>
                              <X size={12} /> Dismiss
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  isEditing ? (
                    <EditRow dark={dark} value={editing[p.photo_id]} onChange={(v) => setEditing((s) => ({ ...s, [p.photo_id]: v }))}
                      onSave={() => confirmOne(p, editing[p.photo_id])} onCancel={() => setEditing((s) => { const n = { ...s }; delete n[p.photo_id]; return n; })}
                      saving={savingId === p.photo_id} pid={p.photo_id} />
                  ) : (
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-xs ${textMuted}`} data-testid={`label-none-${p.photo_id}`}>
                        {isUnavailable ? "No suggestion available" : "No label yet"}
                      </span>
                      {!readOnly && (
                        <button data-testid={`label-manual-${p.photo_id}`} onClick={() => setEditing((s) => ({ ...s, [p.photo_id]: "" }))}
                          className={`text-xs shrink-0 ${dark ? "text-amber" : "text-brand"} hover:underline`}>
                          <Pencil size={12} className="inline mr-0.5" /> Label manually
                        </button>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EditRow({ value, onChange, onSave, onCancel, saving, dark, pid }) {
  return (
    <div className="flex items-center gap-1.5 mt-0.5">
      <Input data-testid={`label-edit-input-${pid}`} autoFocus value={value} maxLength={120}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") onSave(); if (e.key === "Escape") onCancel(); }}
        placeholder="e.g. Kitchen — under-sink cabinet"
        className={`h-9 rounded-lg text-sm ${dark ? "bg-white/10 border-white/15 text-white placeholder:text-white/40" : ""}`} />
      <Button data-testid={`label-edit-save-${pid}`} onClick={onSave} disabled={saving || !value.trim()}
        className="h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-3 shrink-0">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
      </Button>
      <Button data-testid={`label-edit-cancel-${pid}`} variant="ghost" onClick={onCancel}
        className={`h-9 rounded-lg px-2 shrink-0 ${dark ? "text-white/60" : ""}`}><X size={14} /></Button>
    </div>
  );
}
